/**
 * W10d — THE CHART'S GEOMETRY, as a pure function.
 *
 * ⚠️ NO COLOUR HERE AND NONE IN THE COMPONENT EITHER. Every stroke is `currentColor`, which is
 * what `WeekLine` already does: the CSS token decides, so both themes follow for free and the
 * drift ruler that forbids literal colours stays green. A hex in a chart is a hex that is right
 * in one theme.
 *
 * ⚠️ AND THE SERIES IS PLOTTED AGAINST TIME, NOT AGAINST INDEX. Evenly spacing logged sets would
 * draw a training block with one session a fortnight identically to one with four a week — the
 * shape a coach reads would be a lie about the tempo.
 */

export type Point = { atMs: number; value: number };

/** How far past the data the value axis runs, as a fraction of the series' own span. */
export const AXIS_MARGIN = 0.1;

/**
 * 🔴 ONE READING IS A STAT TILE, NOT A CHART — and this is the one-answer rule seen from the
 * drawing side.
 *
 * A "chart" of a single point is a dot in an empty frame: it draws axes, a window and a shape
 * around a number that has no trend, and it invites a coach to read a direction that was never
 * measured. Two readings are the minimum that can show a change, which is the only thing a line
 * is for. The screen draws the TILE below that threshold and the CHART at or above it — the same
 * boundary `oneAnswer` uses for `single` vs `delta`, so a card and its chart can never disagree
 * about whether there is a trend.
 */
export function needsChart(pointCount: number): boolean {
  return pointCount >= 2;
}

export type Box = { width: number; height: number; pad: number };

export type Plotted = {
  /** `d` for a `<path>`, or null when there is no LINE to draw (fewer than two points). */
  path: string | null;
  /** Every point, placed — so a single reading still gets its dot. */
  dots: { x: number; y: number; point: Point }[];
  /** The value axis actually used, after the flat-series rule below. */
  min: number;
  max: number;
};

/**
 * Place a series in a box.
 *
 * 🔴 THE FLAT-SERIES TRAP, AND IT IS WHY THIS IS A TESTED FUNCTION. The obvious scale is
 * `(v - min) / (max - min)`, and when every reading is equal that denominator is **zero**: every
 * coordinate becomes `NaN`, the `d` attribute becomes the string "NaN", and the browser draws
 * **nothing at all** — no error, no warning, an empty chart that looks exactly like "no data".
 * A bodyweight that has not moved in four weeks is the commonest real series there is.
 * A flat series is drawn as a flat line through the middle of the box.
 *
 * ⚠️ TIME COLLAPSES THE SAME WAY. Two readings at the same instant, or one reading, give a zero
 * time span; the points are spread evenly instead, which is the only honest thing left to do.
 */
export function plot(points: readonly Point[], box: Box): Plotted {
  const sorted = [...points].sort((a, b) => a.atMs - b.atMs);
  const inner = { w: box.width - box.pad * 2, h: box.height - box.pad * 2 };

  if (sorted.length === 0) return { path: null, dots: [], min: 0, max: 0 };

  const values = sorted.map((p) => p.value);
  let min = Math.min(...values);
  let max = Math.max(...values);
  if (max === min) {
    // A flat series still has a value; give the axis a nominal span so the line lands mid-box.
    min = min - 1;
    max = max + 1;
  } else {
    // 🔴 THE AXIS STARTS WHERE THE DATA'S MEANING STARTS, NEVER AT ZERO. A bodyweight chart with a
    // zero baseline draws a 74→73 kg month as a flat line one pixel below the top: the change a
    // coach is looking for is 1.3 % of the axis and invisible. Zero is not a meaningful weight, so
    // it is not a meaningful origin. The margin keeps the extremes off the frame's edge, where a
    // marker would be clipped in half.
    const span = max - min;
    min -= span * AXIS_MARGIN;
    max += span * AXIS_MARGIN;
  }

  const t0 = sorted[0]!.atMs;
  const t1 = sorted[sorted.length - 1]!.atMs;
  const span = t1 - t0;

  const dots = sorted.map((p, i) => {
    const fx = span === 0
      ? (sorted.length === 1 ? 0.5 : i / (sorted.length - 1))
      : (p.atMs - t0) / span;
    const fy = (p.value - min) / (max - min);
    return {
      x: box.pad + fx * inner.w,
      // SVG's y grows DOWNWARD; a chart drawn without this inversion is upside down and looks
      // plausible, which is worse than looking broken.
      y: box.pad + (1 - fy) * inner.h,
      point: p,
    };
  });

  const path = dots.length < 2
    ? null
    : "M " + dots.map((d) => `${round(d.x)} ${round(d.y)}`).join(" L ");

  return { path, dots, min, max };
}

/** Two decimals is under a tenth of a pixel at these sizes and keeps the DOM readable. */
function round(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Is every coordinate finite?
 *
 * ⚠️ A GUARD THE COMPONENT CALLS BEFORE RENDERING, because the failure this catches is SILENT:
 * one NaN anywhere in `d` and the browser draws nothing. Cheap, and it turns an invisible chart
 * into a sentence the coach can act on.
 */
export function isDrawable(p: Plotted): boolean {
  return p.dots.every((d) => Number.isFinite(d.x) && Number.isFinite(d.y));
}

/* ═══════════════════════════════════════════════════════════════════════════════════════════════
 * TWO SERIES IN ONE FRAME — and why `plot` called twice is WRONG for that.
 *
 * 🔴 **THE DEFECT THIS FIXES, MEASURED 2026-09-19.** `MetricChart` plotted the measured series and
 * the estimated-1RM series with two SEPARATE calls to `plot`, and `plot` scales a series to fill
 * the box using its OWN min and max. So a weight series spanning 60–70 kg and an e1RM series
 * spanning 80–95 kg were each stretched to the same height: the two lines came out nearly on top
 * of one another, the dashed estimate hidden under the solid line, and the vertical distance
 * between them — the only thing a second series is FOR — meant nothing at all. The morning's
 * verdict recorded it as *"no visible Est. 1RM dashed line"*; the line was there, drawn on a
 * different ruler in the same picture.
 *
 * ⚠️ **TWO SCALES IN ONE FRAME IS THE SAME SIN `BodyMetricsPanel` REFUSES ONE LEVEL UP** — its
 * own header says *"TWO CHARTS, NEVER ONE WITH TWO AXES … every crossing of the lines is an
 * artefact of where the axes were pinned"*. That rule was being broken by the component that
 * quoted it, because the sin was hidden inside a helper called twice.
 * ═══════════════════════════════════════════════════════════════════════════════════════════ */

/** Both series placed against ONE value axis and ONE time axis. */
export type PlottedPair = {
  main: Plotted;
  /** Null when there is no second series. */
  second: Plotted | null;
  /** The axis both were drawn against — what the labels must print. */
  min: number;
  max: number;
  /** The time window both were drawn across. */
  fromMs: number;
  toMs: number;
};

/**
 * Place one or two series against a SHARED axis.
 *
 * ⚠️ THE TIME AXIS IS SHARED TOO, and that is not a detail: the e1RM series is derived from the
 * same sets, so it usually spans the same days — but a set with reps and no weight produces a
 * measured point and NO estimate, so the two can end on different days. Scaled apart, the dashed
 * line would run to the right-hand edge while the solid one stopped short, drawing a divergence
 * that is an artefact of the frame.
 */
export function plotPair(main: readonly Point[], second: readonly Point[] | null, box: Box): PlottedPair {
  const all = second === null ? [...main] : [...main, ...second];
  if (all.length === 0) {
    return { main: plot([], box), second: null, min: 0, max: 0, fromMs: 0, toMs: 0 };
  }
  const values = all.map((p) => p.value);
  let min = Math.min(...values);
  let max = Math.max(...values);
  if (max === min) { min -= 1; max += 1; }
  else { const span = max - min; min -= span * AXIS_MARGIN; max += span * AXIS_MARGIN; }

  const times = all.map((p) => p.atMs);
  const fromMs = Math.min(...times);
  const toMs = Math.max(...times);

  const place = (pts: readonly Point[]): Plotted => {
    const sorted = [...pts].sort((a, b) => a.atMs - b.atMs);
    const inner = { w: box.width - box.pad * 2, h: box.height - box.pad * 2 };
    const tSpan = toMs - fromMs;
    const dots = sorted.map((p, i) => {
      const fx = tSpan === 0
        ? (sorted.length === 1 ? 0.5 : i / (sorted.length - 1))
        : (p.atMs - fromMs) / tSpan;
      const fy = (p.value - min) / (max - min);
      return { x: box.pad + fx * inner.w, y: box.pad + (1 - fy) * inner.h, point: p };
    });
    const path = dots.length < 2 ? null
      : "M " + dots.map((d) => `${round(d.x)} ${round(d.y)}`).join(" L ");
    return { path, dots, min, max };
  };

  return { main: place(main), second: second === null ? null : place(second), min, max, fromMs, toMs };
}

/**
 * The value axis' own labels — THREE, and no more.
 *
 * ⚠️ THREE IS THE JUDGEMENT AND IT IS STATED RATHER THAN TUNED. The chart is 110 px tall; a tick
 * every 20 px is a ladder of numbers taller than the shape it annotates, which is the "a value on
 * every point" anti-pattern moved to the axis. Bottom, middle, top answers *"what is this chart
 * of, and how big is the change"* — which is the whole job of a value axis on a sparkline-sized
 * frame.
 */
export function valueTicks(min: number, max: number): number[] {
  return [min, (min + max) / 2, max];
}

/**
 * The time axis' labels — the FIRST and LAST day, and nothing between.
 *
 * ⚠️ AND IT IS THE DATA'S OWN DAYS, NOT A ROUND CALENDAR DATE. A tick at "Sep 1" on a series that
 * starts on the 4th invites a coach to read a gap that is only the label's. Two ends say what the
 * window is; the crosshair already answers any question about a point inside it.
 *
 * ⚠️ RETURNS NOTHING WHEN THE TWO ENDS ARE THE SAME DAY — printing one date twice, at both ends
 * of an axis, reads as a range of zero rather than as a single day.
 */
export function timeTicks(fromMs: number, toMs: number): { atMs: number; anchor: "start" | "end" }[] {
  const a = new Date(fromMs).toISOString().slice(0, 10);
  const b = new Date(toMs).toISOString().slice(0, 10);
  if (a === b) return [];
  return [{ atMs: fromMs, anchor: "start" }, { atMs: toMs, anchor: "end" }];
}
